import { Injectable } from "@angular/core";
import { INgsiMapBaseEntity } from "../../ngsild/interfaces/ngsi-entity.interface";
import { ILeafletInstance } from "../leaflet.interface";

@Injectable({
  providedIn: "root",
})
export class CommonMapHelperService {
  static readonly maxIcons: number = 50;
  static readonly hueStep: number = Math.floor(
    360 / CommonMapHelperService.maxIcons,
  ); // Step size to ensure distinct colors
  static readonly generatedHues: number[] = [];
  static readonly minimumHueDifference: number = 30; // Minimum hue difference for first 20 colors
  static currentHue: number = 0; // Tracks the last used hue

  static simpleColorGenerator(): string {
    const saturation = 100; // High saturation for vibrant colors
    const lightness = 75; // Brighter lightness for nicer-looking colors
    let hue: number = CommonMapHelperService.currentHue;

    // Ensure minimum hue difference for the first 50 icons
    if (
      CommonMapHelperService.generatedHues.length <
      CommonMapHelperService.maxIcons
    ) {
      let retries = 0;
      while (
        CommonMapHelperService.generatedHues.some(
          (existingHue) =>
            Math.abs(existingHue - hue) <
            CommonMapHelperService.minimumHueDifference,
        )
      ) {
        hue = (hue + CommonMapHelperService.hueStep) % 360; // Adjust the hue to ensure a minimum difference
        retries++;
        if (retries > 30) break; // Prevent infinite loop if conditions are tough to meet
      }
    }

    // Generate the color using HSL
    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

    // Add the hue to the list of generated hues and update currentHue
    CommonMapHelperService.generatedHues.push(hue);
    CommonMapHelperService.currentHue =
      (hue + CommonMapHelperService.hueStep) % 360;

    return color;
  }

  // Method to clear generated hues
  static clearGeneratedHues(): void {
    CommonMapHelperService.generatedHues.length = 0; // Clear the array
    CommonMapHelperService.currentHue = 0; // Reset current hue
  }

  static ignoreIncomingEntity(
    entity: INgsiMapBaseEntity,
    instance: ILeafletInstance,
  ): boolean {
    let ignore = false;

    const storedEntity = instance.entities.get(entity.id);
    if (storedEntity) {
      const storedEntitySource = storedEntity["source"];

      // Ignore ST:PREDICT for marker updates
      if (entity?.["source"] === "ST:PREDICT") {
        ignore = true;
      }

      if (storedEntitySource !== "ST:PREDICT") {
        const incomingDateObserved = new Date(entity.dateObserved["@value"]);
        const storedDateObserved = new Date(
          storedEntity.dateObserved["@value"],
        );

        // If the incoming entity's dateObserved is older, ignore it
        if (incomingDateObserved < storedDateObserved) {
          ignore = true;
        }
      }
    }

    // Check additional (temporär) entities
    const storedAdditionalEntity = instance.additionalEntities.get(entity.id);
    if (storedAdditionalEntity) {
      const incomingDateObserved = new Date(entity.dateObserved["@value"]);
      const storedDateObserved = new Date(
        storedAdditionalEntity.dateObserved["@value"],
      );

      // If the incoming entity's dateObserved is older, ignore it
      if (incomingDateObserved < storedDateObserved) {
        ignore = true;
      }
    }

    return ignore;
  }

  static getMarkerColor(marker: L.Marker<any>): string | undefined {
    let markerColor: string | undefined;

    // ais-tracksymbol marker
    if ("color" in marker.options) {
      markerColor = marker.options.color as string;
    }

    // canvas marker
    if (
      marker.options.icon?.options &&
      "color" in marker.options.icon.options
    ) {
      markerColor = marker.options.icon.options.color as string;
    }

    // FontAwesome Icon
    if (!markerColor) {
      const iconElement = marker.getElement()?.querySelector("i");
      if (iconElement) {
        markerColor = iconElement.style.color; // Get the inline style color
      }
    }

    return markerColor;
  }

  static selectColorForMarker(color?: string, defaultColor?: string): string {
    if (color) {
      return color;
    } else if (defaultColor) {
      return defaultColor;
    } else {
      return CommonMapHelperService.simpleColorGenerator();
    }
  }
}
